import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import withLoader from '../../components/Loader/withLoader.js';
import analyticsApi from '../../api/analytics.js';

// Actions
import { actions as routingActions } from '../../ducks/routing.js';
import { actions as authActions } from '../../ducks/auth.js';
import { actions as appActions } from '../../ducks/app.js';
import { actions as customerActions } from '../../ducks/customer.js';

// Components
import BuildIcon from '@material-ui/icons/BuildOutlined';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import WorkRequestsChildLayout from './WorkRequestsChildLayout.js';
import TileLink from '../../components/TileLink/TileLink';
import MessageCard from '../../components/MessageCard/MessageCard.js';
import ReplaceContact from '../../components/Replace/ReplaceContact/ReplaceContact';
import { getString } from '../../helpers/stringHelpers.js';
import { getWorkRequestAPITypeFromString } from '../../helpers/workRequestHelpers';
import { getLoadedStatus } from '../../helpers/loaderHelpers';

const stringsComponent = 'workRequests';

class WorkRequestsChild extends React.Component {
  constructor(props) {
    super(props);
    this.buildWorkRequestTiles = this.buildWorkRequestTiles.bind(this);
    this.setFilter = this.setFilter.bind(this);

    this.state = {
      filter: '',
    };
  }

  componentDidMount() {
    const { setAppBarTitle } = this.props;

    setAppBarTitle(getString(stringsComponent, 'TITLE'));
  }

  shouldComponentUpdate(nextProps) {
    // Only rerender if not in a unloaded status, or the previous status was loaded (to allow for refresh triggers)
    // This prevents the flash effect as APIs are loaded.
    if (
      getLoadedStatus(this.getLoadedStatuses(nextProps)) !== 'UNLOADED' ||
      getLoadedStatus(this.getLoadedStatuses(this.props)) === 'LOADED'
    ) {
      return true;
    }
    return false;
  }

  getLoadedStatuses(props = this.props) {
    const { tenancy, workRequests, contactDetails, type } = props;
    const loaded = [
      tenancy.loaded,
      workRequests[getWorkRequestAPITypeFromString(type)].loaded,
      contactDetails.loaded,
    ];

    return loaded;
  }

  buildWorkRequestTiles() {
    const { goWorkRequestsItem, type, workRequests, toggleContactModal } =
      this.props;

    const { filter } = this.state;

    const requests = workRequests[getWorkRequestAPITypeFromString(type)].data;
    let jobs = [];
    requests.forEach((request, index) => {
      if (!filter || request.raisedBy === filter) {
        const jobsCount = request.jobs.length;
        const location =
          jobsCount > 1 ? 'Multiple areas' : request.jobs[0].location;
        let description = null;
        if (jobsCount > 1) {
          let currentLocation = '';
          let currentAmenity = '';
          let newLocation = true;
          let newAmenity = true;
          description = (
            <React.Fragment key={index}>
              {request.jobs.map((job, index) => {
                newLocation = currentLocation != job.location;
                newAmenity = currentAmenity != job.amenity;
                currentLocation = job.location;
                currentAmenity = job.amenity;
                return (
                  <React.Fragment key={index}>
                    {newLocation && index !== 0 && <br />}
                    {newLocation && <strong>{job.location}: </strong>}
                    {!newAmenity && !newLocation ? ' | ' : null}
                    {job.amenity} - {job.issue}
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          );
        } else {
          description = (
            <React.Fragment>{request.jobs[0].issue}</React.Fragment>
          );
        }
        jobs.push(
          <TileLink
            title={location + ' - Reference ' + request.referenceNumber}
            content={description}
            icon={BuildIcon}
            action={() => goWorkRequestsItem(type, request.referenceNumber)}
          />
        );
      }
    });
    if (jobs.length === 0) {
      jobs.push(
        <MessageCard icon={InfoIcon} iconColour="#1A8A72">
          {
            <ReplaceContact
              content={getString(
                stringsComponent,
                type == 'previous'
                  ? 'NO_COMPLETED_MAINTENANCE_TEXT'
                  : type == 'cancelled'
                  ? 'NO_CANCELLED_MAINTENANCE_TEXT'
                  : 'NO_CURRENT_MAINTENANCE_TEXT'
              )}
              action={toggleContactModal}
              forceEnglish={true}
              isLink={true}
            />
          }
        </MessageCard>
      );
    }

    return jobs;
  }

  setFilter(value) {
    this.setState({ filter: value });
  }

  render() {
    const {
      signout,
      inOutage,
      outageFrom,
      outageTo,
      outageMessage,
      toggleContactModal,
      onLoadComplete,
      goWorkRequests,
      type,
    } = this.props;

    const WrappedWorkRequestsChildLayout = withLoader(WorkRequestsChildLayout);

    const loaded = this.getLoadedStatuses();

    let filters = [];

    // Only show filters on the 'current' maintenance view.
    if (type === 'current') {
      filters = [
        {
          label: getString(stringsComponent, 'FILTER_LABEL_ALL'),
          filterValue: '',
        },
        {
          label: getString(stringsComponent, 'FILTER_LABEL_ONLINE'),
          filterValue: 'MyKāingaOra',
        },
      ];
    }

    return (
      <WrappedWorkRequestsChildLayout
        loaded={loaded}
        onError={signout}
        inOutage={inOutage}
        outageFrom={outageFrom}
        outageTo={outageTo}
        outageMessage={
          outageMessage && (
            <ReplaceContact
              content={outageMessage}
              action={toggleContactModal}
              isLink={true}
            />
          )
        }
        failureText={
          <ReplaceContact
            content={portalContent.APP_ERROR_MESSAGE}
            action={toggleContactModal}
            isLink={true}
          />
        }
        notFoundText={
          <ReplaceContact
            content={portalContent.APP_NOT_FOUND_MESSAGE}
            action={toggleContactModal}
            isLink={true}
          />
        }
        onLoadComplete={onLoadComplete}
        backAction={goWorkRequests}
        filters={filters}
        filterAction={this.setFilter}
        currentFilter={this.state.filter}
        requests={this.buildWorkRequestTiles()}
        title={
          type == 'previous'
            ? getString(stringsComponent, 'CLOSED_MAINTENANCE_TAB')
            : type == 'cancelled'
            ? getString(stringsComponent, 'CANCELLED_MAINTENANCE_TAB')
            : getString(stringsComponent, 'OPEN_MAINTENANCE_TAB')
        }
      />
    );
  }
}

const mapStateToProps = (state) => ({
  tenancy: state.customer.tenancy,
  workRequests: state.customer.workRequests,
  contactDetails: state.customer.contactDetails,
  inOutage: state.app.inOutage,
  outageFrom: state.app.outageFrom,
  outageTo: state.app.outageTo,
  outageMessage: state.app.outageMessage,
  type: state.location.payload.type,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      ...routingActions,
      ...appActions,
      ...authActions,
      ...customerActions,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(WorkRequestsChild);
